import { h } from 'preact'
import PropTypes from 'prop-types'
import { useState, useEffect, useMemo } from 'preact/hooks'
import PNFO from 'jlinc-shared/PNFO'

import { useOrganizationsIAdmin } from 'lib/membershipAppStateHooks'
import { useOrganizationTopics } from 'lib/topicHooks'
import { useOrganizationFeedSubscriptions } from 'lib/feedSubscriptionHooks'

import StyleishModal from 'components/StyleishModal'
import Select from 'components/Select'
import Button from 'components/Button'
import Header from 'components/Header'
import './index.sass'

export default function ManageOrganizationFeedSubscriptionsModal({
  organization, open, onClose, className = '',
}){
  return <StyleishModal
    className={`ManageOrganizationFeedSubscriptionsModal ${className}`}
    scrollInternally
    open={open}
    onClose={onClose}
    title={`Subscribe to ${organization.name}`}
    padded={false}
    autoFocusWithin={false}
  >
    <Content {...{organization, onClose}}/>
  </StyleishModal>
}

ManageOrganizationFeedSubscriptionsModal.propTypes = {
  className: PropTypes.string,
  organization: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

// Flatten an organization's topic tree into selectable forum channels.
// Skips category headers and nests subtopics as "Parent / Child".
function toChannels(topics = []){
  const channels = []
  topics.forEach(topic => {
    if (!topic.is_category) channels.push({ uid: topic.uid, name: topic.name })
    const subtopics = topic.subtopics || []
    subtopics.forEach(subtopic => {
      if (!subtopic.is_category)
        channels.push({ uid: subtopic.uid, name: `${topic.name} / ${subtopic.name}` })
    })
  })
  return channels
}

const Content = function({ organization, onClose }){
  const { organizationsIAdmin } =
    useOrganizationsIAdmin('ManageOrganizationFeedSubscriptionsModal')
  const adminHubs = organizationsIAdmin.filter(hub => hub.apikey !== organization.apikey)

  // which of my hubs is importing the posts (the subscriber)
  const [subscriberApikey, setSubscriberApikey] = useState('')
  useEffect(() => {
    if (!subscriberApikey && adminHubs.length) setSubscriberApikey(adminHubs[0].apikey)
  }, [adminHubs, subscriberApikey])

  const { organizationTopics: sourceTopics } =
    useOrganizationTopics(organization.apikey, 'ManageOrganizationFeedSubscriptionsModal:source')
  // fall back to the source apikey until a subscriber is chosen, to avoid a wasted
  // request for an empty apikey (the table is gated on subscriberApikey anyway)
  const { organizationTopics: destTopics } =
    useOrganizationTopics(subscriberApikey || organization.apikey, 'ManageOrganizationFeedSubscriptionsModal:dest')

  const {
    organizationFeedSubscriptions,
    subscribeToOrganizationTopic,
    unsubscribeFromOrganizationTopic,
    errorSubscribingToOrganizationFeed,
    errorUnsubscribingFromOrganizationFeed,
  } = useOrganizationFeedSubscriptions(organization.apikey, 'ManageOrganizationFeedSubscriptionsModal')

  const sourceChannels = useMemo(() => toChannels(sourceTopics), [sourceTopics])
  const destChannels = useMemo(() => toChannels(destTopics), [destTopics])

  // existing (sourceChannel → myChannel) subscriptions from this hub into the chosen subscriber hub
  const existingSubscriptions = useMemo(
    () => organizationFeedSubscriptions.filter(s =>
      s.organizationApikey === organization.apikey &&
      s.subscriberOrganizationApikey === subscriberApikey
    ),
    [organizationFeedSubscriptions, organization.apikey, subscriberApikey]
  )

  // selections: Map<sourceChannelUid, myChannelUid>, seeded from existing subscriptions
  const [selections, setSelections] = useState(new Map())
  const existingKey = existingSubscriptions
    .map(s => `${s.topicUid}:${s.subscriberTopicUid}`).sort().join(',')
  useEffect(() => {
    const seeded = new Map()
    existingSubscriptions.forEach(s => seeded.set(s.topicUid, s.subscriberTopicUid))
    setSelections(seeded)
    // re-seed whenever the chosen subscriber hub or its existing subs change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingKey, subscriberApikey])

  const [saving, setSaving] = useState(false)

  const toggleRow = sourceUid => {
    const next = new Map(selections)
    if (next.has(sourceUid)) next.delete(sourceUid)
    else next.set(sourceUid, destChannels[0] ? destChannels[0].uid : '')
    setSelections(next)
  }

  const setRowDestination = (sourceUid, destUid) => {
    const next = new Map(selections)
    next.set(sourceUid, destUid)
    setSelections(next)
  }

  const handleSave = () => {
    setSaving(true)
    try {
      const existingBySource = new Map(existingSubscriptions.map(s => [s.topicUid, s]))
      // subscribe the newly checked rows (and re-point rows whose destination changed)
      selections.forEach((destUid, sourceUid) => {
        if (!destUid) return
        const existing = existingBySource.get(sourceUid)
        if (existing && existing.subscriberTopicUid === destUid) return
        if (existing) unsubscribeFromOrganizationTopic(existing)
        subscribeToOrganizationTopic(subscriberApikey, sourceUid, destUid)
      })
      // unsubscribe rows that were unchecked
      existingSubscriptions.forEach(s => {
        if (!selections.has(s.topicUid)) unsubscribeFromOrganizationTopic(s)
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  if (adminHubs.length === 0) {
    return <div className="ManageOrganizationFeedSubscriptionsModal-empty">
      <Header size="sm">
        You need to be an admin of another {PNFO.singular} to subscribe it to {organization.name}.
      </Header>
    </div>
  }

  return <div className="ManageOrganizationFeedSubscriptionsModal-content">
    <label className="ManageOrganizationFeedSubscriptionsModal-hubPicker">
      <span>Import as {PNFO.singular}:</span>
      <Select {...{
        value: subscriberApikey,
        onChange: setSubscriberApikey,
        options: adminHubs.map(hub => ({ value: hub.apikey, label: hub.name })),
      }}/>
    </label>

    {(errorSubscribingToOrganizationFeed || errorUnsubscribingFromOrganizationFeed) &&
      <div className="ManageOrganizationFeedSubscriptionsModal-error">
        Something went wrong saving your subscriptions. Please try again.
      </div>
    }

    <div className="ManageOrganizationFeedSubscriptionsModal-table">
      <div className="ManageOrganizationFeedSubscriptionsModal-tableHead">
        <span>{organization.name} channel</span>
        <span>Your channel</span>
      </div>
      {sourceChannels.length === 0
        ? <div className="ManageOrganizationFeedSubscriptionsModal-empty">
            This {PNFO.singular} has no forum channels to subscribe to.
        </div>
        : sourceChannels.map(channel => {
          const checked = selections.has(channel.uid)
          return <div key={channel.uid} className="ManageOrganizationFeedSubscriptionsModal-row">
            <label className="ManageOrganizationFeedSubscriptionsModal-sourceChannel">
              <input type="checkbox" checked={checked} onChange={() => toggleRow(channel.uid)}/>
              <span>#{channel.name}</span>
            </label>
            <Select {...{
              className: 'ManageOrganizationFeedSubscriptionsModal-destChannel',
              disabled: !checked,
              value: checked ? (selections.get(channel.uid) || '') : '',
              placeholder: 'Select channel',
              onChange: destUid => setRowDestination(channel.uid, destUid),
              options: destChannels.map(c => ({ value: c.uid, label: c.name })),
            }}/>
          </div>
        })
      }
    </div>

    <div className="ManageOrganizationFeedSubscriptionsModal-actions">
      <Button {...{ type: 'normal', value: 'Cancel', onClick: onClose, disabled: saving }}/>
      <Button {...{
        type: 'primary',
        value: saving ? 'Saving…' : 'Subscribe',
        onClick: handleSave,
        disabled: saving,
      }}/>
    </div>
  </div>
}

Content.propTypes = {
  organization: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}
